import type { LDContext } from '@launchdarkly/react-sdk';

const LD_BOOT_CONTEXT_STORAGE_KEY = 'ldBootContext';
// Mesmo nome de AuthToken.REFRESH_TOKEN (api-lib); some no logout e quando a sessão é invalidada (401/403).
const REFRESH_TOKEN_STORAGE_KEY = 'cobli-refresh-token';
const LD_BOOT_CONTEXT_TTL_MS = 7 * 24 * 60 * 60 * 1000;

interface StoredLdBootContext {
  context: LDContext;
  savedAt: number;
}

export const LD_ANONYMOUS_CONTEXT: LDContext = { kind: 'user', anonymous: true, key: 'anonymous' };

/** Chave estável do contexto: mesmo usuário, frota e atributos geram a mesma string. */
export const getLdContextKey = (context: LDContext) => JSON.stringify(context);

/**
 * Último contexto identificado neste navegador. O client do LaunchDarkly nasce com ele, então a
 * avaliação de flags (e o cache local delas) corre em paralelo com a sessão em vez de esperar o identify.
 */
export const readLdBootContext = (): LDContext | null => {
  try {
    const raw = window.localStorage.getItem(LD_BOOT_CONTEXT_STORAGE_KEY);
    if (!raw) return null;
    const stored = JSON.parse(raw) as StoredLdBootContext | null;
    const now = Date.now();
    const savedAt = stored?.savedAt;
    // Contém dados do usuário: só vale enquanto a sessão existir e por tempo limitado (relógio recuado não estende o TTL).
    const isValid =
      !!window.localStorage.getItem(REFRESH_TOKEN_STORAGE_KEY) &&
      typeof savedAt === 'number' &&
      Number.isFinite(savedAt) &&
      savedAt <= now &&
      now - savedAt <= LD_BOOT_CONTEXT_TTL_MS &&
      !!stored?.context &&
      typeof stored.context === 'object' &&
      'kind' in stored.context;
    if (!isValid) {
      window.localStorage.removeItem(LD_BOOT_CONTEXT_STORAGE_KEY);
      return null;
    }
    return stored.context;
  } catch {
    return null;
  }
};

export const writeLdBootContext = (context: LDContext) => {
  try {
    const stored: StoredLdBootContext = { context, savedAt: Date.now() };
    window.localStorage.setItem(LD_BOOT_CONTEXT_STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Sem storage o próximo boot só volta a começar anônimo.
  }
};

export const clearLdBootContext = () => {
  try {
    window.localStorage.removeItem(LD_BOOT_CONTEXT_STORAGE_KEY);
  } catch {
    // Idem.
  }
};

const IS_DEMO_MODE = process.env.REACT_APP_DEMO_MODE === 'true';

/** Contexto com que o client do LaunchDarkly é criado no carregamento do módulo. */
export const ldInitialContext: LDContext = (!IS_DEMO_MODE && readLdBootContext()) || LD_ANONYMOUS_CONTEXT;

/**
 * Contexto em que o client está agora. Logout/login são SPA e o client sobrevive a eles, então comparar
 * com `ldInitialContext` deixava A com as flags de B depois de A → B → A na mesma aba.
 */
let ldClientContextKey: string | null = getLdContextKey(ldInitialContext);

export const getLdClientContextKey = () => ldClientContextKey;

/** Registra no pedido: o SDK aplica os identify em ordem, então o último pedido é o contexto final. */
export const setLdClientContextKey = (key: string | null) => {
  ldClientContextKey = key;
};
